/**
 * use-quote-job-actions — Revise and Split for the open quote (docs/job/GOAL.md
 * C2, C3, A18).
 *
 * The one-open-quote model of App.tsx is kept: every Job action is a server
 * transaction on the open quote, followed by a RELOAD of the quote the operator
 * should now see. Three rules make that safe, and each is a rule this codebase
 * learned the hard way:
 *
 *   1. SAVE FIRST. Unsaved edits are saved before the server acts, so Revise
 *      freezes and Split partitions what the operator is looking at.
 *   2. SEND THE SAVE TOKEN, THEN RELOAD. Revise and Split advance
 *      projects.updated_at. Adopting the new token without reloading would arm
 *      an autosave of the pre-change content (CLAUDE.md "updated_at is the
 *      save token"), so the fresh row is always loaded through the full load
 *      path, which rehydrates every piece of state.
 *   3. NO MEMOISED CLOSURES. App passes getters and refs, never values, so a
 *      stale render cannot pin an old project (CLAUDE.md "a memoised callback
 *      must never pin the routing/load chain").
 *
 * The flows are plain async functions (runRevise, runSplit) so they are tested
 * without React; the hook only adds busy state and the capability check.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
    getQuoteJob,
    getQuoteJobsCapabilities,
    listQuoteRevisions,
    reviseQuote,
    splitQuote,
    type QuoteJobMember,
    type QuoteJobView,
    type QuoteRevision,
    type QuoteRevisionSummary,
    type ReviseResult,
    type SplitResult,
} from '../services/quote-jobs/quote-jobs-client';
import { splitReviewMessage, type SplitCopyPlan } from '../services/quote-jobs/split-copy';
import { checkRevisionReason, type RevisionReason } from '../services/quote-jobs/revision-reason';
import { prepareRevisionAttachments } from '../services/quote-jobs/revision-attachments';
import { buildJobBarModel, jobBarVisible } from '../services/quote-jobs/job-bar-model';
import {
    QUOTE_RUN_ACTIVE,
    QUOTE_RUN_REVISE_REFUSED_MESSAGE,
    QUOTE_RUN_SPLIT_REFUSED_MESSAGE,
} from '../services/quote-run-open-guard';

export interface QuoteIdentity {
    qmQuoteNo: string | null;
    quoteRev: number;
    sourceQuoteRef: string | null;
    /** The Quote # as the operator reads it, e.g. 'RBB260605 Rev 1'. */
    label: string;
    emailSent: boolean;
}

export interface QuoteJobActionDeps {
    getCurrentProjectId: () => string | null;
    getSaveToken: () => string | null;
    hasUnsavedChanges: () => boolean;
    saveCurrentProject: () => Promise<unknown>;
    getProject: (id: string) => Promise<any>;
    reloadProject: (project: any) => Promise<unknown>;
    refreshProjects: () => Promise<unknown>;
    notify: (notice: { kind: 'success' | 'error' | 'info'; message: string }) => void;
    newId: () => string;
    /** scopeFingerprintForQuoteAuthoring from utils/auto-pipeline. */
    fingerprintScope: (scope: unknown[]) => string;
    /**
     * Add the PDFs and photos picked for a New revision to the reloaded quote,
     * through the ordinary Stage 5 reference-file path (App's
     * handleAddReferenceFiles), tagged with the new Rev. Resolves with the
     * names of the files it did not add, and only once React has committed
     * the added ones, because runRevise saves straight after and the save
     * reads the committed state. Absent: those files are not added, and the
     * outcome names them.
     */
    addRevisionFiles?: (files: File[], origin: { addedFrom: 'revision'; quoteRev: number }) => Promise<{ rejected?: string[] } | unknown>;
    /**
     * True while a Quote Run is active (App's batchActiveRef, read at call
     * time). Revise and Split end in a reload of the open quote, which the run
     * refuses, so they refuse first: nothing is saved or sent to the server.
     * Absent: no Quote Run can be active.
     */
    isQuoteRunActive?: () => boolean;
}

export type JobActionOutcome =
    | { ok: true; projectId: string; message: string }
    | { ok: false; code: string; message: string };

function errorOutcome(error: any, fallback: string): JobActionOutcome {
    const code = String(error?.code || error?.body?.error || 'JOB_ACTION_FAILED');
    const serverMessage = typeof error?.body?.message === 'string' ? error.body.message : null;
    const message = code === 'PROJECT_STALE_WRITE'
        ? 'This quote changed on another device. Open it again, then try once more.'
        : (serverMessage || (typeof error?.message === 'string' && error.message) || fallback);
    return { ok: false, code, message };
}

/**
 * Decision D1, A3 review round 2. A Quote Run owns the loaded state, so the
 * reload that ends a Revise or Split is refused. Refuse before the server
 * changes anything: after it, the screen would keep the old revision with a
 * spent save token, and a Split's new quote would never be shown.
 */
function quoteRunRefusal(deps: QuoteJobActionDeps, message: string): JobActionOutcome | null {
    if (deps.isQuoteRunActive?.() !== true) return null;
    return { ok: false, code: QUOTE_RUN_ACTIVE, message };
}

async function saveIfDirty(deps: QuoteJobActionDeps): Promise<JobActionOutcome | null> {
    if (!deps.hasUnsavedChanges()) return null;
    try {
        await deps.saveCurrentProject();
    } catch (error) {
        return errorOutcome(error, 'Your changes could not be saved, so nothing else was changed.');
    }
    if (deps.hasUnsavedChanges()) {
        return { ok: false, code: 'SAVE_INCOMPLETE', message: 'Your changes are still saving. Try again in a moment.' };
    }
    return null;
}

export function nextRevisionLabel(identity: QuoteIdentity): string {
    return `Rev ${identity.quoteRev + 1}`;
}

export interface ReviseInput {
    /**
     * Why the quote is revised (Lucid 007, JOBS-UX-SPEC 3.3): { kind, note,
     * siteVisitNeeded? }. The New revision sheet and the QM Assistant both
     * send one; the server still takes a Revise without one, so an old tab
     * keeps working.
     */
    reason?: RevisionReason | null;
    /** Files picked in the New revision sheet. The Assistant sends none. */
    files?: File[];
}

export type ReviseOutcome = JobActionOutcome & {
    result?: ReviseResult;
    /** True only when the server echoed the reason: the response's reasonEvent has a reasonKind. */
    reasonSaved?: boolean;
    /** Files that were not added after the reload, by name. */
    fileWarnings?: string[];
};

function fileNamesOf(files: File[]): string[] {
    return files.map((file) => String(file?.name || '').trim() || 'a file');
}

/** "1 file was not added: a.jpg. Add it from Reference Files." */
function filesNotAddedSentence(names: string[]): string {
    const one = names.length === 1;
    return `${names.length} ${one ? 'file was' : 'files were'} not added: ${names.join(', ')}. Add ${one ? 'it' : 'them'} from Reference Files.`;
}

/**
 * New revision (JOBS-UX-SPEC 3.3). Nothing changes on the server until the
 * reason is checked, the files are read and unsaved edits are saved; then one
 * Revise request carries the reason and the emails and text files. PDFs and
 * photos are added after the reload, and a failure there leaves the revision
 * standing and names the file.
 */
export async function runRevise(deps: QuoteJobActionDeps, input: ReviseInput = {}): Promise<ReviseOutcome> {
    const projectId = deps.getCurrentProjectId();
    if (!projectId) return { ok: false, code: 'NO_QUOTE', message: 'Open a quote first.' };
    const runBusy = quoteRunRefusal(deps, QUOTE_RUN_REVISE_REFUSED_MESSAGE);
    if (runBusy) return runBusy;

    let reason: Required<RevisionReason> | null = null;
    if (input.reason) {
        const checked = checkRevisionReason(input.reason.kind, input.reason.note, input.reason.siteVisitNeeded);
        if (!checked.ok) return { ok: false, code: 'REVISION_REASON_INVALID', message: checked.problem };
        reason = checked.reason;
    }
    const files = reason && Array.isArray(input.files) ? input.files : [];
    const prepared = await prepareRevisionAttachments(files);
    if (!prepared.ok) return { ok: false, code: 'REVISION_ATTACHMENT_UNREADABLE', message: prepared.problem };

    const blocked = await saveIfDirty(deps);
    if (blocked) return blocked;
    // A run that started during the save refuses the same way.
    const runStarted = quoteRunRefusal(deps, QUOTE_RUN_REVISE_REFUSED_MESSAGE);
    if (runStarted) return runStarted;
    const token = deps.getSaveToken();
    if (!token) return { ok: false, code: 'NO_SAVE_TOKEN', message: 'Save this quote once before revising it.' };
    let result: ReviseResult;
    try {
        result = reason
            ? await reviseQuote(projectId, token, {
                reason,
                ...(prepared.attachments.length > 0 ? { attachments: prepared.attachments } : {}),
                ...(prepared.otherFileNames.length > 0 ? { otherFileNames: prepared.otherFileNames } : {}),
            })
            : await reviseQuote(projectId, token);
    } catch (error) {
        return errorOutcome(error, 'The revision could not be made. Nothing was changed.');
    }
    const fresh = await deps.getProject(projectId);
    await deps.reloadProject(fresh || result.project);
    await deps.refreshProjects();
    const rev = Number(result.project?.quoteRev ?? 0);

    // PDFs and photos go through the ordinary Stage 5 reference-file path, on
    // the quote that is now loaded, tagged with the Rev they came with.
    let fileWarnings: string[] = [];
    let unsavedFileNames: string[] = [];
    if (prepared.otherFiles.length > 0) {
        if (typeof deps.addRevisionFiles !== 'function') {
            fileWarnings = fileNamesOf(prepared.otherFiles);
        } else {
            try {
                const added: any = await deps.addRevisionFiles(prepared.otherFiles, { addedFrom: 'revision', quoteRev: rev });
                fileWarnings = Array.isArray(added?.rejected) ? added.rejected.map(String) : [];
            } catch {
                fileWarnings = fileNamesOf(prepared.otherFiles);
            }
        }
        // Save them now. They were added inside the post-load guard the reload
        // above armed, where App's dirty tracker re-baselines instead of
        // marking the quote dirty, so the autosave would never store them.
        if (fileWarnings.length < prepared.otherFiles.length) {
            try {
                await deps.saveCurrentProject();
            } catch {
                const rejected = new Set(fileWarnings);
                unsavedFileNames = fileNamesOf(prepared.otherFiles).filter((name) => !rejected.has(name));
            }
        }
    }

    const reasonSaved = result.reasonEvent?.reasonKind != null;
    const frozenLabel = result.frozenRevision?.displayQuoteNo;
    const sentences = [`Rev ${rev} is ready to edit.`];
    if (reason) {
        sentences.push(reasonSaved
            ? 'The reason is saved and added to the Reference Files.'
            : 'The reason was not saved. Add it again after the update.');
    } else if (frozenLabel) {
        sentences.push(`${frozenLabel} stays on record exactly as it was.`);
    }
    if (fileWarnings.length > 0) sentences.push(filesNotAddedSentence(fileWarnings));
    if (unsavedFileNames.length > 0) {
        sentences.push(`The added files are not saved yet: ${unsavedFileNames.join(', ')}. Save the quote before you close it.`);
    }
    return {
        ok: true,
        projectId,
        result,
        reasonSaved,
        ...(fileWarnings.length > 0 ? { fileWarnings } : {}),
        message: sentences.join(' '),
    };
}

export interface SplitInput {
    itemIds: string[];
    areaName: string;
    /** The open quote's Stage 4 scope and Stage 5 items, used only to fingerprint each half. */
    scope: any[];
    /**
     * What the Split does to the Objective and the Note, planned by the editor
     * from what it shows (services/quote-jobs/split-copy.ts). Absent: both are
     * copied as they are, as before.
     */
    copy?: SplitCopyPlan | null;
    /**
     * Why, when the quote was issued: the split then makes a new revision of
     * what stays (JOBS-UX-SPEC 3.2). Absent for a quote that was never issued.
     */
    reason?: RevisionReason | null;
}

function idOf(item: any): string {
    return typeof item?.id === 'string' ? item.id.trim() : '';
}

export async function runSplit(deps: QuoteJobActionDeps, input: SplitInput): Promise<JobActionOutcome & { result?: SplitResult }> {
    const projectId = deps.getCurrentProjectId();
    if (!projectId) return { ok: false, code: 'NO_QUOTE', message: 'Open a quote first.' };
    const runBusy = quoteRunRefusal(deps, QUOTE_RUN_SPLIT_REFUSED_MESSAGE);
    if (runBusy) return runBusy;
    const blocked = await saveIfDirty(deps);
    if (blocked) return blocked;
    // A run that started during the save refuses the same way.
    const runStarted = quoteRunRefusal(deps, QUOTE_RUN_SPLIT_REFUSED_MESSAGE);
    if (runStarted) return runStarted;
    const token = deps.getSaveToken();
    if (!token) return { ok: false, code: 'NO_SAVE_TOKEN', message: 'Save this quote once before splitting it.' };
    let reason: Required<RevisionReason> | null = null;
    if (input.reason) {
        const checked = checkRevisionReason(input.reason.kind, input.reason.note, input.reason.siteVisitNeeded);
        if (!checked.ok) return { ok: false, code: 'REVISION_REASON_INVALID', message: checked.problem };
        reason = checked.reason;
    }

    const moving = new Set(input.itemIds);
    const scope = Array.isArray(input.scope) ? input.scope : [];
    const targetScope = scope.filter(item => moving.has(idOf(item)));
    const sourceScope = scope.filter(item => !moving.has(idOf(item)));
    const targetProjectId = deps.newId();
    const objective = input.copy?.objective || null;
    const review = input.copy?.review;
    const askReview = Boolean(splitReviewMessage(review));
    let result: SplitResult;
    try {
        result = await splitQuote(projectId, {
            expectedUpdatedAt: token,
            targetProjectId,
            itemIds: input.itemIds,
            areaName: input.areaName,
            derived: {
                source: {
                    sourceAuthoringFingerprint: deps.fingerprintScope(sourceScope),
                    ...(objective ? { scopeObjective: objective.source } : {}),
                },
                target: {
                    sourceAuthoringFingerprint: deps.fingerprintScope(targetScope),
                    ...(objective ? { scopeObjective: objective.target } : {}),
                },
            },
            ...(askReview && review ? { review: { objective: review.objective === true, note: review.note === true } } : {}),
            ...(reason ? { reason } : {}),
        });
    } catch (error) {
        return errorOutcome(error, 'The quote could not be split. Nothing was changed.');
    }

    const target = await deps.getProject(targetProjectId);
    await deps.reloadProject(target || result.target);
    await deps.refreshProjects();
    const notes: string[] = [];
    if (result.quoteNumberWarning === 'QM_MONTHLY_OVERFLOW') {
        notes.push('This month has no Quote # left, so the new quote has none yet.');
    }
    if (result.skippedMedia.length > 0) {
        notes.push(`${result.skippedMedia.length} photo${result.skippedMedia.length === 1 ? ' was' : 's were'} no longer stored and did not carry over.`);
    }
    if (result.sourceAdvanced) {
        // An issued quote that lost items became its next revision, unsent.
        const rev = Number(result.source?.quoteRev);
        const revWords = Number.isInteger(rev) && rev > 0 ? `Rev ${rev}` : 'its next revision';
        notes.push(`The original quote is now ${revWords} because it had already been issued.`);
    }
    // Only what the server kept: its split event carries the reason.
    if (result.reasonEvent?.reasonKind != null) notes.push('The reason is saved.');
    // The server has the last word: it also flags an Objective the stored
    // quote marks as the operator's, which the browser may not have known.
    const reviewNote = splitReviewMessage(result.target?.quoteData?.meta?.splitReview) || splitReviewMessage(review);
    if (reviewNote) notes.push(reviewNote);
    const title = String(result.target?.title || 'the new quote');
    return {
        ok: true,
        projectId: targetProjectId,
        result,
        message: [`Split done. Now showing "${title}".`, ...notes].join(' '),
    };
}

export function useQuoteJobActions(deps: QuoteJobActionDeps, options: { userKey: string | null }) {
    const depsRef = useRef(deps);
    depsRef.current = deps;
    const [enabled, setEnabled] = useState(false);
    const [busy, setBusy] = useState<'revise' | 'split' | null>(null);
    // Bumped to ask the server again. Is Jobs on for this business? The answer
    // is asked when the person signs in and again each time the Stage 5
    // Actions menu opens (recheckCapability), so New revision, Split and
    // Revisions appear once Jobs is switched on, and go once it is switched
    // off, without a reload. The same discipline as the Menu's IMPORT QUOTE
    // row (BottomNavBar.tsx): rendering never waits for the answer, the last
    // answer stays on screen while the next one is asked, and a failed ask is
    // "unknown", not "no", so a network drop does not hide the entries.
    const [capabilityAsk, setCapabilityAsk] = useState(0);
    const userKeyRef = useRef(options.userKey);
    userKeyRef.current = options.userKey;

    // Another person (or signed out): forget the last person's answer first.
    useEffect(() => {
        setEnabled(false);
    }, [options.userKey]);

    useEffect(() => {
        let cancelled = false;
        if (!options.userKey) return undefined;
        getQuoteJobsCapabilities()
            .then((caps) => { if (!cancelled) setEnabled(caps.enabled); })
            // Unknown: keep the answer on screen; the next menu open asks again.
            .catch(() => undefined);
        // A newer ask (or a person change) supersedes this one's answer.
        return () => { cancelled = true; };
    }, [options.userKey, capabilityAsk]);

    const recheckCapability = useCallback(() => {
        if (!userKeyRef.current) return;
        setCapabilityAsk((count) => count + 1);
    }, []);

    const revise = useCallback(async (input?: ReviseInput) => {
        if (busy) return null;
        setBusy('revise');
        try {
            const outcome = await runRevise(depsRef.current, input);
            depsRef.current.notify({ kind: outcome.ok ? 'success' : 'error', message: outcome.message });
            return outcome;
        } finally {
            setBusy(null);
        }
    }, [busy]);

    const split = useCallback(async (input: SplitInput) => {
        if (busy) return null;
        setBusy('split');
        try {
            const outcome = await runSplit(depsRef.current, input);
            depsRef.current.notify({ kind: outcome.ok ? 'success' : 'error', message: outcome.message });
            return outcome;
        } finally {
            setBusy(null);
        }
    }, [busy]);

    return { enabled, busy, revise, split, recheckCapability };
}

// ── The open quote's Job and revisions ──────────────────────────────────────

const EMPTY_OPEN_QUOTE_JOB: OpenQuoteJobState = Object.freeze({ jobView: null, revisions: [] as QuoteRevisionSummary[] }) as OpenQuoteJobState;

export interface OpenQuoteJobState {
    jobView: QuoteJobView | null;
    revisions: QuoteRevisionSummary[];
}

/**
 * Load the Job and the frozen revisions of the open quote. Keyed by the
 * project, its Job and a refresh key (the summary's updatedAt), so a revise or
 * split, which advances updated_at, reloads them. A failed read leaves the
 * editor without Job extras rather than showing an error: the quote itself is
 * unaffected.
 */
export function useOpenQuoteJob(input: {
    enabled: boolean;
    projectId: string | null;
    quoteJobId: string | null;
    refreshKey: string | null;
}): OpenQuoteJobState {
    // Tagged with the quote it belongs to, so another quote's revisions are
    // never shown (or compared) for the one just opened while its own load.
    const [state, setState] = useState<OpenQuoteJobState & { projectId: string | null }>(
        { jobView: null, revisions: [], projectId: null },
    );
    useEffect(() => {
        let cancelled = false;
        if (!input.enabled || !input.projectId) {
            setState({ jobView: null, revisions: [], projectId: null });
            return undefined;
        }
        const projectId = input.projectId;
        Promise.all([
            listQuoteRevisions(projectId).catch(() => [] as QuoteRevisionSummary[]),
            input.quoteJobId ? getQuoteJob(input.quoteJobId).catch(() => null) : Promise.resolve(null),
        ]).then(([revisions, jobView]) => {
            if (!cancelled) setState({ jobView, revisions, projectId });
        });
        return () => { cancelled = true; };
    }, [input.enabled, input.projectId, input.quoteJobId, input.refreshKey]);
    return state.projectId === input.projectId
        ? { jobView: state.jobView, revisions: state.revisions }
        : EMPTY_OPEN_QUOTE_JOB;
}

// ── The editor prop ─────────────────────────────────────────────────────────

const DECIDED = new Set(['won', 'lost']);

export interface QuoteJobEditorPropInput {
    enabled: boolean;
    busy: 'revise' | 'split' | null;
    currentProjectId: string | null;
    quoteData: any;
    /** The open quote's summary row (projects list), for status and Job id. */
    summary: any;
    jobView: QuoteJobView | null;
    revisions: QuoteRevisionSummary[];
    formatLabel: (input: { qmQuoteNo?: string | null; quoteRev?: number | null; sourceQuoteRef?: string | null }) => string;
    onRevise: (input?: ReviseInput) => Promise<unknown>;
    onSplit: (input: { itemIds: string[]; areaName: string; copy?: SplitCopyPlan | null }) => Promise<unknown>;
    onOpenQuote: (projectId: string) => void;
    loadRevision: (id: string) => Promise<QuoteRevision>;
}

/** A Job member's status as the Job strip words it. The QM Assistant's list_job_quotes uses the same words. */
export function memberTone(member: QuoteJobMember): { statusLabel: string; statusTone: 'finish' | 'send' | 'waiting' | 'won' | 'lost' } {
    const decided = String(member.quoteStatus || '').toLowerCase();
    if (decided === 'won') return { statusLabel: 'Won', statusTone: 'won' };
    if (decided === 'lost') return { statusLabel: 'Lost', statusTone: 'lost' };
    if (member.emailSent) return { statusLabel: 'Sent', statusTone: 'waiting' };
    if (member.pdfGenerated || Number(member.currentStage) >= 5) return { statusLabel: 'Ready to send', statusTone: 'send' };
    return { statusLabel: 'In progress', statusTone: 'finish' };
}

/**
 * Everything the Stage 5 editor needs for Jobs, as plain data and callbacks.
 * Null when Jobs are off, so the editor renders exactly as before.
 */
export function buildQuoteJobEditorProp(input: QuoteJobEditorPropInput) {
    if (!input.enabled || !input.currentProjectId) return null;
    const meta = input.quoteData?.meta || {};
    const quoteRev = Number(meta.quoteRev ?? input.summary?.quoteRev ?? 0) || 0;
    const qmQuoteNo = meta.qmQuoteNo || input.summary?.qmQuoteNo || null;
    const sourceQuoteRef = meta.sourceQuoteRef || input.summary?.sourceQuoteRef || null;
    const label = input.formatLabel({ qmQuoteNo, quoteRev, sourceQuoteRef });
    const decided = DECIDED.has(String(input.summary?.quoteStatus || '').toLowerCase());
    const emailSent = input.summary?.emailSent === true;
    const pdfGenerated = input.summary?.pdfGenerated === true;
    const imported = Boolean(sourceQuoteRef || meta.importSource);
    const printed = Boolean(input.summary?.aiContext?.lastQuotePrintOpenedAt);
    const itemCount = Array.isArray(input.quoteData?.scope) ? input.quoteData.scope.length : 0;
    const numbered = Boolean(qmQuoteNo || sourceQuoteRef);
    const jobView = input.jobView;
    const canRevise = numbered && !decided && (emailSent || pdfGenerated || printed || imported || quoteRev > 0);
    // The Job bar (JOBS-UX-SPEC 1.1): on whenever the quote has a Job, a Rev
    // above 0, a kept copy, or can be revised. A draft never issued and with
    // no Job shows none; its Split stays in the Tools menu.
    const bar = jobBarVisible({
        hasJob: Boolean(input.summary?.quoteJobId || jobView?.job),
        quoteRev,
        keptRevisionCount: Array.isArray(input.revisions) ? input.revisions.length : 0,
        canRevise,
    })
        ? buildJobBarModel({
            jobView,
            currentProjectId: input.currentProjectId,
            summary: input.summary,
            quoteData: input.quoteData,
            formatLabel: input.formatLabel,
        })
        : null;
    const status = String(input.summary?.quoteStatus || '').toLowerCase();
    const textOrNull = (value: unknown) => (typeof value === 'string' && value.trim() ? value : null);
    return {
        enabled: true,
        busy: input.busy,
        /** The open quote's Job, or null while it is a quote on its own. */
        jobId: input.summary?.quoteJobId || jobView?.job?.id || null,
        identity: { label, quoteRev, qmQuoteNo, sourceQuoteRef },
        // What stays after a split of an issued quote becomes (GOAL A12). ''
        // from formatLabel means no Quote # yet, so the revision alone.
        nextLabel: input.formatLabel({ qmQuoteNo, quoteRev: quoteRev + 1, sourceQuoteRef }) || `Rev ${quoteRev + 1}`,
        // Offered once the quote has left the office in some form; a quote
        // that was never issued is simply edited. The server also refuses a
        // decided or unnumbered quote.
        canRevise,
        canSplit: !decided && itemCount >= 2,
        issued: emailSent || pdfGenerated,
        revisions: input.revisions,
        loadRevision: input.loadRevision,
        onRevise: input.onRevise,
        onSplit: input.onSplit,
        onOpenQuote: input.onOpenQuote,
        jobTitle: jobView?.job?.title || null,
        // The editor prop contract of JOBS-UX-SPEC 1.5, read by the Job bar
        // and by the reason and warning work (slice C).
        bar,
        siteVisitRequestedAt: textOrNull(input.summary?.siteVisitRequestedAt),
        issuedBy: (emailSent ? 'sent' : pdfGenerated ? 'pdf' : null) as 'sent' | 'pdf' | null,
        issuedAt: emailSent
            ? textOrNull(input.summary?.emailSentAt)
            : pdfGenerated ? textOrNull(input.summary?.pdfGeneratedAt) : null,
        quoteStatus: status || null,
        decided: (status === 'won' || status === 'lost' ? status : null) as 'won' | 'lost' | null,
        historyKey: textOrNull(input.summary?.updatedAt),
    };
}
